import { ChangeDetectionStrategy, Component, DestroyRef, afterNextRender, inject, signal } from '@angular/core';
import { NgOptimizedImage, provideImageKitLoader } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, map, of, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FlameLogoComponent } from "../../flame-logo/flame-logo.component";
import { SocialsComponent } from "../../socials/socials.component";
import { NextShowComponent } from "../../next-show/next-show.component";
import { BookingComponent } from "./booking.component";
import { ShowDetailComponent } from "./show-detail.component";
import { HotBackend } from "../../services/hot-backend";
import { Show } from "../../models/show";
import { dayKey } from "../../utils/show-date";

// Past this the buttons land without a next show rather than staying hidden.
const SHOWS_TIMEOUT_MS = 2000;

@Component({
  selector: 'app-home-page',
  imports: [NgOptimizedImage, FlameLogoComponent, SocialsComponent, NextShowComponent, BookingComponent, ShowDetailComponent],
  templateUrl: './home-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideImageKitLoader(environment.imageKitCdnUrl)
  ]
})
export class HomePage {
  private readonly backend = inject(HotBackend);
  private readonly destroyRef = inject(DestroyRef);

  bookingOpen = signal(false);
  selectedShow = signal<Show | null>(null);
  nextShow = signal<Show | null>(null);
  landed = signal(false);

  constructor() {
    afterNextRender(() => {
      this.backend
        .getShows()
        .pipe(
          timeout(SHOWS_TIMEOUT_MS),
          map(nextUpcoming),
          catchError(() => of(null)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((show) => {
          this.nextShow.set(show);
          this.landed.set(true);
        });
    });
  }

  openBooking() { this.bookingOpen.set(true); }
  closeBooking() { this.bookingOpen.set(false); }

  scrollToMore() {
    document.getElementById('more')?.scrollIntoView({ behavior: 'smooth' });
  }
}

// Local calendar day as YYYY-MM-DD, the same shape as the venue-local `datetime` prefix.
function nextUpcoming(shows: Show[]): Show | null {
  const today = new Date().toLocaleDateString('en-CA');
  const upcoming = shows.filter((s) => s.datetime && dayKey(s.datetime) >= today);
  return upcoming.sort((a, b) => a.datetime.localeCompare(b.datetime))[0] ?? null;
}
